import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  useToast,
  useColorModeValue,
  Spinner,
  Text,
  Center,
} from '@chakra-ui/react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useUser } from '../../contexts/UserContext';
import OrganizationSurveyForm from './OrganizationSurveyForm';
import { formatOrganizationSurveyTitle, getSurveyPeriodLabel } from '../../utils/organizationSurveyHelper';

const SurveyConfigurationPage = () => {
  const { organizationId, organizationSurveyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isTourMode } = useUser();

  const isHub = location.pathname.startsWith('/hub');
  const organizationIdentifier = isHub ? "me" : organizationId;
  const surveyIdentifier = organizationSurveyId;

  const [formData, setFormData] = useState({});
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [availableSurveys, setAvailableSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const surveyTitle = formatOrganizationSurveyTitle(formData);
  const surveyPeriodLabel = getSurveyPeriodLabel(formData);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [surveyRes, periodsRes, configRes] = await Promise.all([
          axios.get('/surveys'),
          axios.get('/survey_periods'),
          axios.get(`/organizations/${organizationIdentifier}/organization_surveys/${surveyIdentifier}`)
        ]);

        const config = configRes.data;
        setAvailableSurveys(surveyRes.data);
        setAvailablePeriods(periodsRes.data);

        setFormData({
          ...config,
          start_date: config.start_date || '',
          end_date: config.end_date || '',
          opening_message: config.opening_message || '',
          closing_message: config.closing_message || '',
          roles: config.roles || '',
          passcode: config.passcode || '',
          endpoint: config.endpoint || '',
          survey_id: config.survey?.id || '',
          survey_period_id: config.survey_period?.id || '',
          locked: config.locked || false,
        });
      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Error loading configuration:', error);
          toast({ title: 'Failed to load survey data.', status: 'error', duration: 3000, isClosable: true });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationIdentifier, surveyIdentifier, toast]);

  const handleSubmit = async (updatedData) => {
    const submission = { ...updatedData };
    if (isHub) {
      delete submission.endpoint;
      delete submission.survey_period_id;
    }
    try {
      await axios.put(
        `/organizations/${organizationIdentifier}/organization_surveys/${surveyIdentifier}`,
        submission
      );
      toast({ title: 'Survey configuration saved.', status: 'success', duration: 3000, isClosable: true });
      navigate(isHub ? `/hub/surveys` : `/app/organizations/${organizationId}/surveys`);
    } catch (error) {
      if (error?.__tourReadOnly || error?.response?.data?.error === "tour_read_only") {
        return;
      }
      console.error('Error saving survey configuration:', error);
      toast({ title: 'Failed to save survey configuration.', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleToggleLock = async () => {
    try {
      const action = formData.locked ? 'unlock' : 'lock';
      const res = await axios.patch(`/organizations/${organizationIdentifier}/organization_surveys/${surveyIdentifier}/${action}`);
      setFormData((prev) => ({ ...prev, locked: res.data.locked }));
      toast({
        title: res.data.locked ? 'Survey locked.' : 'Survey unlocked.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      if (error?.__tourReadOnly || error?.response?.data?.error === "tour_read_only") {
        return;
      }
      toast({
        title: 'Failed to update lock status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const cardBg = useColorModeValue('white', 'gray.800');

  if (loading) {
    return (
      <Center height="70vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (notFound) {
    return (
      <Center height="70vh">
        <Box textAlign="center">
          <Heading as="h2" size="lg" mb={4}>
            Survey not found
          </Heading>
          <Text color="gray.600">The requested survey configuration could not be loaded. Please check the link or contact support.</Text>
        </Box>
      </Center>
    );
  }

  return (
    <Box mx="auto" p={6} maxW="container.md">
      <Heading as="h2" size="lg" mb={6} textAlign="center">
        Survey Configuration
      </Heading>

      <Card bg={cardBg} shadow="md" borderRadius="lg">
        <CardHeader pb={0}>
          <Heading as="h4" size="md">
            Manage Survey Settings
          </Heading>
        </CardHeader>
        <CardBody>
          <Box bg="gray.50" borderRadius="md" p={4} mb={6}>
            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={3}>
              Survey Instance
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  Survey
                </Text>
                <Text fontWeight="semibold">{surveyTitle || 'N/A'}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                  Survey Period
                </Text>
                <Text fontWeight="semibold">{surveyPeriodLabel || 'N/A'}</Text>
              </Box>
            </SimpleGrid>
          </Box>

          <OrganizationSurveyForm
            formTitle="Survey Configuration"
            initialData={formData}
            availablePeriods={availablePeriods}
            availableSurveys={availableSurveys}
            onSubmit={handleSubmit}
            isHub={isHub}
            isTour={isTourMode}
            isSubmitting={false}
            onToggleLock={handleToggleLock}
            isLocked={formData.locked}
          />
        </CardBody>
      </Card>
    </Box>
  );
};

export default SurveyConfigurationPage;
